import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AI_BOT_ID, AI_BOT_NAME } from '@/lib/aiBot';

interface Member {
  id: string;
  name: string;
}

interface MentionPopupProps {
  roomId: string;
  query: string;
  onSelect: (member: Member) => void;
  onClose: () => void;
}

const MentionPopup = ({ roomId, query, onSelect, onClose }: MentionPopupProps) => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMembers = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('chat_room_members')
        .select('user_id')
        .eq('room_id', roomId);

      if (data) {
        const userIds = data.map((m) => m.user_id);
        const { data: profiles } = await supabase
          .from('profiles_public' as any)
          .select('id, name')
          .in('id', userIds) as any;

        const list: Member[] = (profiles || []).map((p) => ({ id: p.id, name: p.name }));

        // Check if AI bot is enabled in this room
        const { data: roomData } = await supabase
          .from('chat_rooms')
          .select('ai_bot_enabled')
          .eq('id', roomId)
          .maybeSingle();

        if (roomData?.ai_bot_enabled) {
          list.push({ id: AI_BOT_ID, name: AI_BOT_NAME });
        }

        setMembers(list);
      }
      setLoading(false);
    };

    fetchMembers();
  }, [roomId]);

  const filtered = members.filter((m) =>
    m.name.toLowerCase().includes(query.toLowerCase())
  );

  if (loading || filtered.length === 0) return null;

  return (
    <div className="absolute bottom-full left-0 right-0 mb-1 mx-2 bg-white rounded-lg shadow-lg border border-gray-200 max-h-48 overflow-y-auto z-20">
      {filtered.map((member) => (
        <button
          key={member.id}
          onClick={() => onSelect(member)}
          className="w-full text-right px-4 py-2.5 hover:bg-[#f0f2f5] transition-colors flex items-center gap-2 text-sm text-[#111b21]"
        >
          <div className="w-8 h-8 rounded-full bg-[#DFE5E7] flex items-center justify-center text-xs font-medium text-[#54656f] shrink-0">
            {member.id === AI_BOT_ID ? '🤖' : member.name.charAt(0)}
          </div>
          <span className="truncate">{member.name}</span>
        </button>
      ))}
    </div>
  );
};

export default MentionPopup;
